import { Either, Left, Right } from 'fp-ts/lib/Either'
import superagent from 'superagent'

import { log } from '@/log'
import { ApiClient, ApiClientWithAuth } from '@/services/api/client'
import { ApiService } from '@/services/api/service'
import { InitializeSession } from '@/services/session/initialize-session'

const namespace: string = 'auth'

export interface UserRegistrationPayload {
  username: string
  password: string
  email: string
  first_name?: string
  last_name?: string
  phonetic_first_name?: string
  phonetic_last_name?: string
  zipcode?: string
  region_id?: string | null
  address1?: string
  address2?: string
  tel?: string
  is_delivery?: boolean
  display_level?: number
  // TODO: 必要？
  registered_at: string
}

export default class Auth extends ApiService {

  /**
   * Register a new user
   * As as side-effect, authentication-related cookie is set
   */
  async register(payload: UserRegistrationPayload): Promise<Either<Error, superagent.Response>> {
    try {
      return await InitializeSession.onInitialized(async () => {
        /**
         * もしこのメソッドが呼ばれた時点で仮会員でなければ、ひとまず仮会員になってもらってから本会員登録に進むよう、
         * ApiClientWithAuthを利用している。
         *
         * 本会員登録API実行時には仮会員状態からの情報の引き継ぎ（ゲーム履歴からのクーポン発行など）を行っているため、
         * APIの事前条件として仮会員であることを前提としたい。
         * GrowthPaletteとしてあらゆる機能を利用するうえで仮会員として人格の同一性の確保を期待したいし、
         * 実際2023/07時点であらゆるページで仮会員登録が実行されているので、事実上そのように振る舞っている。
         *
         * @see https://github.com/my-color/front/issues/7455#issuecomment-1653526446
         */
        const response = await ApiClientWithAuth
          .post('/api/register', payload)

        log.debug({ service: `${namespace}/register`, response })

        return new Right(response.body.data)
      })
    } catch (error) {
      log.error({ service: `${namespace}/register`, error })

      return new Left(error)
    }
  }

  /**
   * Login to server
   * As a side-effect, authentication-related cookie is set
   * @param username
   * @param password
   */
  async login(
    username: string,
    password: string
  ): Promise<Either<Error, superagent.Response>> {
    try {
      return await InitializeSession.onInitialized(async () => {
        const response = await ApiClient
          .post('/api/login')
          .send({
            username,
            password,
          })

        log.debug({ service: `${namespace}/login`, response })

        return new Right(response.body.data)
      })
    } catch (error) {
      log.error({ service: `${namespace}/login`, error })

      return new Left(error)
    }
  }

  /**
   * Logout
   * As a side-effect, authentication-related cookie is unset
   */
  async logout(): Promise<Either<Error, superagent.Response>> {
    try {
      return await InitializeSession.onInitialized(async () => {
        const response = await ApiClient
          .post('/api/logout')

        log.debug({ service: `${namespace}/logout`, response })

        return new Right(response.body.data)
      })
    } catch (error) {
      log.error({ service: `${namespace}/logout`, error })

      return new Left(error)
    }
  }
}
